import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight, Pencil, Zap } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { money, parseAmountInput } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { AmountField, CopyRow, InfoRow, MethodIcon, Notice, PageLoading, PageTitle, Spinner } from '@/components/ui';
import { PixProfileForm } from '@/components/PixProfileForm';
import { NeedAccount } from './NeedAccount';
import type { Method, PixProfile, Tx, UsdtAddress } from '@/types/api';

const PRESETS: Record<string, number[]> = { BRL: [20, 50, 100, 200, 500], USD: [10, 25, 50, 100, 250], USDT: [10, 25, 50, 100] };

/** Shared amount step: limits, quick chips, the board credit preview and the create call. */
function AmountStep({ method, extra, onCreate, cta }: { method: Method; extra?: React.ReactNode; onCreate: (amount: number) => Promise<void>; cta: string }) {
    const { t, lang } = useI18n();
    const { me } = useAuth();
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const amount = parseAmountInput(text);
    const board = me?.currency || 'BRL';
    const rangeError = text && (!Number.isFinite(amount) ? t('errors.amount_invalid')
        : amount < method.min || (method.max > 0 && amount > method.max) ? t('errors.amount_range', { min: money(method.min, method.currency, lang), max: money(method.max, method.currency, lang) }) : null);
    const credit = Number.isFinite(amount) ? Math.round(amount * method.rate * 100) / 100 : 0;

    const submit = async () => {
        if (!Number.isFinite(amount) || rangeError) { haptic('error'); return; }
        setBusy(true); setError(null);
        try { await onCreate(amount); }
        catch (e) { setError(String(e instanceof Error ? e.message : e)); haptic('error'); }
        finally { setBusy(false); }
    };
    return (
        <div className="space-y-3">
            {extra}
            <AmountField value={text} onChange={v => { setText(v); setError(null); }} currency={method.currency} min={method.min} max={method.max}
                         error={rangeError || null} presets={PRESETS[method.currency]} />
            <div className="card py-3">
                <InfoRow label={t('deposit.limits')} value={`${money(method.min, method.currency, lang)} – ${money(method.max, method.currency, lang)}`} />
                {method.currency !== board && <InfoRow label={t('deposit.rate')} value={`1 ${method.currency} = ${money(method.rate, board, lang)}`} />}
                {Number.isFinite(amount) && amount > 0 && <InfoRow label={t('deposit.youGet')} value={money(credit, board, lang)} strong />}
                {method.bonusPercent > 0 && <InfoRow label={t('deposit.bonus')} value={<span className="badge-lime">+{method.bonusPercent}%</span>} />}
            </div>
            {method.instructions && <Notice>{method.instructions}</Notice>}
            {error && <Notice tone="warn">{error}</Notice>}
            <button className="btn-primary w-full" disabled={busy || !text || !!rangeError} onClick={submit}>
                {busy ? <Spinner className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />} {busy ? t('deposit.creating') : cta}
            </button>
        </div>
    );
}

function PixDeposit({ method }: { method: Method }) {
    const { t, te } = useI18n();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const q = useQuery({ queryKey: ['pix', 'profile'], queryFn: async () => (await api.get<PixProfile>('/pix/profile')).data });
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const p = q.data;
    if (p.needed.deposit.length || editing) {
        const fields = editing ? p.asked : p.asked.filter(f => p.needed.deposit.includes(f));
        return <PixProfileForm profile={p} fields={fields.length ? fields : p.needed.deposit} onSaved={() => setEditing(false)} />;
    }
    const payer = (
        <div className="card py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
                <div className="text-[11px] text-ink-muted">{t('pix.payer')}</div>
                <div className="text-sm font-semibold truncate">{p.profile.name}</div>
                <div className="text-[11px] text-ink-muted font-mono">CPF {p.profile.cpf}</div>
            </div>
            <button className="btn-secondary px-3 py-2 text-xs" onClick={() => setEditing(true)}><Pencil className="w-3.5 h-3.5" /> {t('common.edit')}</button>
        </div>
    );
    return (
        <AmountStep method={method} extra={payer} cta={t('deposit.pix.cta')} onCreate={async amount => {
            try {
                const { data } = await api.post<Tx>('/deposit/pix', { amount });
                haptic('success');
                void qc.invalidateQueries({ queryKey: ['transactions'] });
                navigate(`/tx/${data.operationId}`, { replace: true });
            } catch (e) {
                const err = apiError(e);
                if (err.error === 'pix_profile_required') { await q.refetch(); }
                throw new Error(te(err, { currency: method.currency }));
            }
        }} />
    );
}

function CryptoDeposit({ method }: { method: Method }) {
    const { t, te } = useI18n();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const coins = method.coins || [];
    const [coin, setCoin] = useState(coins[0]?.code || '');
    const picker = (
        <div>
            <label className="label">{t('deposit.crypto.coin')}</label>
            <div className="grid grid-cols-2 gap-2">
                {coins.map(c => (
                    <button key={c.code} onClick={() => { haptic('selection'); setCoin(c.code); }}
                        className={clsx('rounded-xl border px-3 py-2.5 text-left transition active:scale-[.98]',
                            coin === c.code ? 'border-brand-400/60 bg-brand-400/10' : 'border-white/10 bg-white/[.03]')}>
                        <div className="text-sm font-bold">{c.label}</div>
                        <div className="text-[11px] text-ink-muted">{c.network}</div>
                    </button>
                ))}
            </div>
        </div>
    );
    return (
        <AmountStep method={method} extra={picker} cta={t('deposit.crypto.cta')} onCreate={async amount => {
            if (!coin) throw new Error(t('errors.invalid_coin'));
            try {
                const { data } = await api.post<Tx>('/deposit/crypto', { coin, amount });
                haptic('success');
                void qc.invalidateQueries({ queryKey: ['transactions'] });
                navigate(`/tx/${data.operationId}`, { replace: true });
            } catch (e) { throw new Error(te(apiError(e))); }
        }} />
    );
}

function UsdtDeposit({ method }: { method: Method }) {
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const q = useQuery({ queryKey: ['deposit', 'usdt'], queryFn: async () => (await api.get<UsdtAddress>('/deposit/usdt')).data, staleTime: 5 * 60_000 });
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const a = q.data;
    return (
        <div className="space-y-3">
            <div className="card flex flex-col items-center gap-3 py-5">
                <img src={a.qr} alt="QR" className="w-48 h-48 rounded-xl bg-white p-2" />
                <span className="badge-teal">USDT · {a.network}</span>
            </div>
            <CopyRow label={t('deposit.usdt.address')} value={a.address} />
            <div className="card py-3">
                <InfoRow label={t('deposit.usdt.min')} value={money(a.min, 'USDT', lang)} />
                <InfoRow label={t('deposit.rate')} value={`1 USDT = ${money(a.rate, me?.currency || 'BRL', lang)}`} />
                {a.bonusPercent > 0 && <InfoRow label={t('deposit.bonus')} value={<span className="badge-lime">+{a.bonusPercent}%</span>} />}
            </div>
            <Notice tone="warn">{t('deposit.usdt.warning')}</Notice>
            <Notice>{t('deposit.usdt.auto')}</Notice>
            {method.instructions && <Notice>{method.instructions}</Notice>}
        </div>
    );
}

function PageDeposit({ method, path }: { method: Method; path: 'link' | 'card' }) {
    const { t, te } = useI18n();
    const navigate = useNavigate();
    const qc = useQueryClient();
    return (
        <AmountStep method={method} cta={t(`deposit.${path}.cta`)} onCreate={async amount => {
            try {
                const { data } = await api.post<Tx>(`/deposit/${path}`, { amount });
                haptic('success');
                void qc.invalidateQueries({ queryKey: ['transactions'] });
                navigate(`/tx/${data.operationId}`, { replace: true });
            } catch (e) { throw new Error(te(apiError(e))); }
        }} />
    );
}

export function DepositMethodPage() {
    const { key } = useParams();
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const q = useQuery({
        queryKey: ['methods', 'deposit', lang],
        queryFn: async () => (await api.get<{ methods: Method[] }>('/deposit/methods')).data.methods,
        enabled: !!me?.registered, staleTime: 30_000
    });
    const method = useMemo(() => q.data?.find(m => m.key === key), [q.data, key]);
    if (!me?.registered) return <NeedAccount />;
    if (q.isLoading) return <PageLoading />;
    if (q.isError) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    if (!method) return <Notice tone="warn">{t('errors.method_unavailable')}</Notice>;
    return (
        <div>
            <div className="flex items-center gap-3 mb-4">
                <MethodIcon kind={method.kind} size="lg" />
                <div>
                    <PageTitle title={method.name} subtitle={t(`kinds.${method.kind}`)} />
                </div>
            </div>
            {method.kind === 'pix' && <PixDeposit method={method} />}
            {method.kind === 'crypto' && <CryptoDeposit method={method} />}
            {method.kind === 'usdt' && <UsdtDeposit method={method} />}
            {method.kind === 'link' && <PageDeposit method={method} path="link" />}
            {method.kind === 'card' && <PageDeposit method={method} path="card" />}
            {method.kind === 'pix' && <p className="text-[11px] text-ink-muted text-center mt-4 flex items-center justify-center gap-1"><Zap className="w-3 h-3" /> {t('deposit.pix.instant')}</p>}
        </div>
    );
}
